<template>
  <div v-if="visible" class="product-selector-dialog__mask">
    <section class="product-selector-dialog" role="dialog" aria-modal="true" aria-label="选择修改商品">
      <header>
        <strong>{{ title }}</strong>
        <button aria-label="关闭" @click="$emit('close')">×</button>
      </header>
      <nav>
        <button :class="{ active: activeTab === 'all' }" @click="$emit('tab-change', 'all')">选择商品</button>
        <button :class="{ active: activeTab === 'selected' }" @click="$emit('tab-change', 'selected')">
          已选择（{{ selectedIds.length }}）
        </button>
      </nav>
      <div v-if="activeTab === 'all'" class="product-selector-dialog__filters">
        <slot name="filters" />
        <el-checkbox :value="pageFullySelected" class="product-selector-dialog__top-all" @change="$emit('toggle-page')">
          全选
        </el-checkbox>
      </div>
      <main>
        <article
          v-for="product in products"
          :key="productKey(product)"
          :class="{ selected: selectedIds.includes(productId(product)) }"
          class="product-selector-dialog__card"
          role="button"
          tabindex="0"
          @click="$emit('toggle-product', product)"
          @keyup.enter="$emit('toggle-product', product)">
          <div class="product-selector-dialog__image">
            <el-image :src="productImage(product)" fit="cover"><div slot="error">📦</div></el-image>
            <input
              :checked="selectedIds.includes(productId(product))"
              type="checkbox"
              @click.stop="$emit('toggle-product', product)" />
          </div>
          <span :title="productTitle(product)">{{ productTitle(product) }}</span>
          <small v-if="product.platform_product_id" :title="String(product.platform_product_id)">
            ID：{{ product.platform_product_id }}
          </small>
          <small>{{ productPrice(product) }}</small>
        </article>
        <p v-if="!loading && !products.length" class="product-selector-dialog__empty">暂无商品</p>
      </main>
      <footer>
        <div class="product-selector-dialog__pager">
          <button :disabled="page <= 1 || loading" @click="$emit('page-change', page - 1)">上一页</button>
          <span>第 {{ page }} / {{ totalPages }} 页，共 {{ total }} 条</span>
          <button :disabled="page >= totalPages || loading" @click="$emit('page-change', page + 1)">下一页</button>
          <el-select
            :value="pageSize"
            size="small"
            popper-class="product-selector-dialog__popper"
            class="product-selector-dialog__page-size"
            @change="$emit('page-size-change', $event)">
            <el-option v-for="size in pageSizes" :key="size" :label="size + '条/页'" :value="size" />
          </el-select>
          <span>跳至</span>
          <input
            :value="jumpPage"
            min="1"
            type="number"
            @input="$emit('jump-input', $event.target.value)"
            @keyup.enter="$emit('jump')" />
          <span>页</span>
          <button :disabled="loading" @click="$emit('jump')">跳转</button>
        </div>
        <div class="product-selector-dialog__actions">
          <button class="plain" @click="$emit('close')">取消</button>
          <button :disabled="!selectedIds.length" @click="$emit('confirm', selectedIds.slice())">
            确定（已选 {{ selectedIds.length }} 件）
          </button>
        </div>
      </footer>
    </section>
  </div>
</template>

<script>
const identity = (value) => value

export default {
  name: 'ProductSelectorDialog',
  props: {
    visible: Boolean,
    title: { type: String, default: '选择修改商品' },
    activeTab: { type: String, default: 'all' },
    products: { type: Array, default: () => [] },
    selectedIds: { type: Array, default: () => [] },
    loading: Boolean,
    page: { type: Number, default: 1 },
    pageSize: { type: Number, default: 20 },
    pageSizes: { type: Array, default: () => [20, 50, 100] },
    total: { type: Number, default: 0 },
    totalPages: { type: Number, default: 1 },
    jumpPage: { type: Number, default: 1 },
    pageFullySelected: Boolean,
    productId: { type: Function, default: identity },
    productKey: { type: Function, default: identity },
    productTitle: { type: Function, default: identity },
    productImage: { type: Function, default: identity },
    productPrice: { type: Function, default: identity }
  }
}
</script>

<style scoped>
.product-selector-dialog__mask {
  position: fixed;
  z-index: 3000;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(3px);
}
.product-selector-dialog {
  display: flex;
  flex-direction: column;
  width: min(1040px, calc(100vw - 40px));
  max-height: calc(100vh - 48px);
  overflow: visible;
  border: 1px solid #dcdfea;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.2);
}
.product-selector-dialog > header {
  display: flex;
  flex-shrink: 0;
  border-radius: 12px 12px 0 0;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #e0e0ec;
  color: #1a1a2e;
  font-size: 16px;
}
.product-selector-dialog > header button {
  width: 24px;
  height: 24px;
  border: 1px solid #e0e0ec;
  border-radius: 4px;
  background: #fff;
  color: #666;
  font-size: 18px;
}
.product-selector-dialog nav {
  display: flex;
  flex-shrink: 0;
  padding: 0 20px;
  border-bottom: 1px solid #e0e0ec;
  background: #fafbff;
}
.product-selector-dialog nav button {
  min-width: 80px;
  padding: 10px 12px;
  border: 0;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: #4b5568;
  font-size: 12px;
}
.product-selector-dialog nav button.active {
  border-bottom-color: #1677ff;
  color: #1677ff;
}
.product-selector-dialog__filters {
  display: flex;
  flex-shrink: 0;
  min-height: 48px;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  border-bottom: 1px solid #eef0f6;
  background: #f8fafc;
  font-size: 11px;
}
.product-selector-dialog__filters ::v-deep(.el-input__inner),
.product-selector-dialog__filters ::v-deep(.el-range-input) {
  font-size: 11px;
}
.product-selector-dialog__top-all {
  display: inline-flex;
  align-items: center;
  color: #1a1a2e;
  font-size: 12px;
  white-space: nowrap;
  cursor: pointer;
}
.product-selector-dialog__top-all {
  margin-left: auto;
  align-self: flex-end;
  flex-shrink: 0;
  padding-bottom: 8px;
}
.product-selector-dialog__top-all ::v-deep(.el-checkbox__inner) {
  width: 14px;
  height: 14px;
  border-color: #b9c1d0;
  border-radius: 3px;
}
.product-selector-dialog__top-all ::v-deep(.el-checkbox__input.is-checked .el-checkbox__inner) {
  border-color: #1677ff;
  background: #1677ff;
}
.product-selector-dialog__top-all ::v-deep(.el-checkbox__label) {
  padding-left: 5px;
  color: inherit;
  font-size: inherit;
}
.product-selector-dialog__image input {
  width: 14px;
  height: 14px;
  margin: 0;
  accent-color: #1677ff;
  cursor: pointer;
}
.product-selector-dialog main {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  align-content: start;
  align-items: stretch;
  gap: 14px;
  min-height: 140px;
  max-height: 430px;
  padding: 18px 20px;
  overflow: auto;
}
.product-selector-dialog__card {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 7px;
  padding: 0 10px 10px;
  overflow: hidden;
  border: 1px solid #d9ddeb;
  border-radius: 8px;
  background: #fff;
  color: #1a1a2e;
  cursor: pointer;
  transition: border-color .15s, box-shadow .15s;
}
.product-selector-dialog__card:hover {
  border-color: #91caff;
  box-shadow: 0 3px 10px rgba(22, 119, 255, .08);
}
.product-selector-dialog__card.selected {
  border-color: #1677ff;
  box-shadow: 0 0 0 1px #1677ff;
  background: #f0f7ff;
}
.product-selector-dialog__image {
  position: relative;
  aspect-ratio: 1 / 1;
  flex-shrink: 0;
  margin: 0 -10px;
  background: #f2f4f7;
}
.product-selector-dialog__image .el-image {
  width: 100%;
  height: 100%;
}
.product-selector-dialog__image input {
  position: absolute;
  bottom: 6px;
  left: 7px;
}
.product-selector-dialog__card span {
  overflow: hidden;
  color: #384055;
  display: -webkit-box;
  min-height: 36px;
  font-size: 12px;
  line-height: 18px;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}
.product-selector-dialog__card small {
  overflow: hidden;
  color: #8c94a3;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.product-selector-dialog__card > small:last-child {
  color: #1d2939;
  font-size: 13px;
  font-weight: 600;
}
.product-selector-dialog__empty {
  grid-column: 1 / -1;
  align-self: center;
  padding: 28px 0;
  color: #999;
  text-align: center;
}
.product-selector-dialog footer {
  display: grid;
  flex-shrink: 0;
  min-height: 64px;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: 16px;
  padding: 12px 20px;
  border-top: 1px solid #e0e0ec;
  border-radius: 0 0 12px 12px;
  background: #fff;
  font-size: 12px;
}
.product-selector-dialog__pager {
  display: flex;
  min-width: 0;
  align-items: center;
  flex-wrap: wrap;
  justify-self: start;
  gap: 6px;
}
.product-selector-dialog__pager input {
  width: 36px;
  height: 28px;
  border: 1px solid #e0e0ec;
  border-radius: 4px;
  text-align: center;
}
.product-selector-dialog__page-size {
  width: 86px;
}
.product-selector-dialog__page-size ::v-deep(.el-input__inner) {
  height: 28px;
  padding: 0 18px 0 6px;
  font-size: 11px;
  line-height: 28px;
}
.product-selector-dialog__page-size ::v-deep(.el-input__icon) {
  line-height: 28px;
}
.product-selector-dialog__pager button {
  height: 28px;
  padding: 0 8px;
  border: 1px solid #d0d5dd;
  border-radius: 4px;
  background: #fff;
  color: #475467;
}
.product-selector-dialog__pager button:disabled,
.product-selector-dialog__actions button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.product-selector-dialog__actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.product-selector-dialog__actions button {
  height: 32px;
  padding: 0 13px;
  border: 0;
  border-radius: 5px;
  background: #9fc7ff;
  color: #fff;
  font-size: 12px;
}
.product-selector-dialog__actions button:not(:disabled):last-child {
  background: #1677ff;
}
.product-selector-dialog__actions button.plain {
  border: 1px solid #d9ddeb;
  background: #fff;
  color: #667085;
}
@media (max-width: 900px) {
  .product-selector-dialog__filters {
    gap: 12px;
  }
  .product-selector-dialog__top-all {
    margin-left: auto;
  }
  .product-selector-dialog main {
    grid-template-columns: repeat(4, minmax(0, 1fr));
    min-height: 100px;
  }
  .product-selector-dialog footer {
    grid-template-columns: 1fr auto;
    gap: 8px;
    padding: 8px 12px;
  }
  .product-selector-dialog__pager {
    grid-row: 1;
    grid-column: 1 / -1;
    justify-self: start;
    flex-wrap: wrap;
  }
  .product-selector-dialog__actions {
    grid-row: 2;
    grid-column: 1 / -1;
    justify-self: end;
  }
}
@media (max-width: 680px) {
  .product-selector-dialog main {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}
</style>

<style>
.product-selector-dialog__popper {
  z-index: 4001 !important;
}
.product-selector-dialog__popper .el-select-dropdown__item,
.product-selector-dialog__popper .el-picker-panel__content,
.product-selector-dialog__popper .el-date-table td {
  font-size: 11px;
}
</style>
